import { useState, useEffect, useCallback } from "react";
import { Switch, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, Alert } from "@mui/material";
import PlanoCard from "../../components/PlanoCard/index.tsx";
import PagamentoModal from "../../components/Modals/PagamentoModal/index.tsx";
import "../../styles/design-system.css";
import { usePagamentoContext } from "../../contexts/PagamentoContext.tsx";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import pagamentoService from "../../services/pagamentosService.ts";
import planosService from "../../services/planosService.ts";
import { MetodoPagamento, AsaasPaymentResponse } from "../../components/Modals/PagamentoModal/types.ts";
import { CreditCardData } from "../../components/Modals/PagamentoModal/CreditCardForm.tsx";

interface PlanosPageProps {
  darkMode?: boolean;
}

// Espelha PlanoModeloResponseDTO do backend (GET /plano). precoAnual e
// valorMensalEquivalente sao calculados la, pela mesma funcao que define o
// valor cobrado na ASAAS (PlanoService.calcularPrecoAnual).
interface PlanoBackend {
  id: string;
  nome: string;
  preco: number;
  precoAnual: number;
  valorMensalEquivalente: number;
}

// Só conteúdo de marketing (título, features, destaque). PREÇO NÃO MORA AQUI:
// os valores anuais chumbados que existiam antes estavam errados (R$ 712,80
// exibido contra R$ 712,98 realmente cobrado, e o mesmo erro nos outros dois
// planos) porque eram uma cópia manual de uma conta que o backend faz. Agora
// preço mensal, anual e mensal-equivalente vêm de GET /plano, que os calcula
// com a mesma função usada para cobrar na ASAAS.
export const PLANOS_DATA = [
  {
    id: "agenda-light",
    titulo: "Agenda Light",
    funcionalidades: [
      "Agendamento de cirurgias",
      "Cadastros completos (Pacientes, Médicos, etc.)",
      "Até 5 usuários",
      "Suporte por WhatsApp",
    ],
  },
  {
    id: "agenda-pro",
    titulo: "Agenda Pró",
    recomendacao: true,
    funcionalidades: [
      "Tudo do Agenda Light",
      "Até 10 usuários",
      "Integração Nativa com ControlSIS",
      "Sincronização automática da Agenda com Vales (Em breve)",
      "Suporte prioritário",
    ],
  },
  {
    id: "agenda-enterprise",
    titulo: "Agenda Enterprise",
    funcionalidades: [
      "Tudo do Agenda Light",
      "Tudo do Agenda Pró",
      "Usuários ilimitados",
      "Suporte Técnico Dedicado",
      "Onboard personalizado",
      "Dashboard Cirurgias",
      "API de integrações com outros ERP",
    ],
  },
];

const PlanosPage = ({ darkMode = false }: PlanosPageProps) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [planosBackend, setPlanosBackend] = useState<PlanoBackend[]>([]);
  const [planosBackendLoading, setPlanosBackendLoading] = useState(true);
  const [planosBackendErro, setPlanosBackendErro] = useState(false);
  const { isPlanActive, planoModeloId, refreshPlano, dataExpiracao, ciclo: cicloAtual, cancelado, isLoading: planoContextLoading } = usePlanoContext();
  const { showFeedback } = useFeedback();

  const [trocaModalOpen, setTrocaModalOpen] = useState(false);
  const [trocaData, setTrocaData] = useState<{
    planoAtualNome: string;
    novoPlanoNome: string;
    novoPlanoBackendId: string;
    ciclo: string;
    imediata: boolean;
  } | null>(null);
  const [loadingTroca, setLoadingTroca] = useState(false);
  const [trocaPaymentData, setTrocaPaymentData] = useState<AsaasPaymentResponse | null>(null);

  const {
    planoSelecionado,
    setPlanoSelecionado,
    modalPagamentoOpen,
    setModalPagamentoOpen,
    setPagamentoAtual,
  } = usePagamentoContext();

  const fetchPlanos = useCallback(async () => {
    setPlanosBackendLoading(true);
    try {
      const res = await planosService.listarPlanos();
      const lista: PlanoBackend[] = res?.data || [];
      setPlanosBackend(lista);
      // Lista vazia conta como falha: sem ela nao ha preco nenhum pra mostrar,
      // e exibir um card sem valor e pior do que avisar que deu errado.
      setPlanosBackendErro(lista.length === 0);
    } catch {
      setPlanosBackend([]);
      setPlanosBackendErro(true);
    } finally {
      setPlanosBackendLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanos();
  }, [fetchPlanos]);

  const getPlanoBackendByNome = (titulo: string): PlanoBackend | null => {
    const match = planosBackend.find(
      (p) => p.nome?.toLowerCase().trim() === titulo.toLowerCase().trim()
    );
    return match || null;
  };

  const handleAssinar = async (plano: typeof PLANOS_DATA[0]) => {
    // Guarda defensiva: enquanto o PlanoContext (dados da assinatura atual) ou
    // a lista de PlanoModelo (planosBackend, buscada localmente nesta tela)
    // ainda não terminaram de carregar, getPlanoModeloIdByNome() retorna null
    // pra tudo e a REGRA C abaixo seria pulada por engano, caindo na REGRA B
    // (nova assinatura) mesmo para quem já tem plano ativo. Os botões já ficam
    // desabilitados nesse período (prop `loading` do PlanoCard), isso aqui é
    // só uma segunda trava caso o clique escape por algum motivo.
    if (planoContextLoading || planosBackendLoading) {
      showFeedback("Carregando dados do seu plano, aguarde um instante.", "error");
      return;
    }

    const ciclo = isAnnual ? "YEARLY" : "MONTHLY";

    const planoBackend = getPlanoBackendByNome(plano.titulo);
    if (!planoBackend) {
      showFeedback("Não foi possível carregar os valores do plano. Recarregue a página e tente novamente.", "error");
      return;
    }

    const preco = isAnnual ? planoBackend.precoAnual : planoBackend.preco;
    const valorMensal = isAnnual ? planoBackend.valorMensalEquivalente : planoBackend.preco;

    const backendPlanoModeloId = planoBackend.id;

    // ═══════════════════════════════════════════════════
    // REGRA C: Plano ATIVO e ID DIFERENTE → troca de plano
    // ═══════════════════════════════════════════════════
    if (isPlanActive && !cancelado && planoModeloId && backendPlanoModeloId && planoModeloId !== backendPlanoModeloId) {
      const planoAtualBackend = planosBackend.find((p: any) => p.id === planoModeloId);
      const planoAtualNome = planoAtualBackend?.nome || "Plano Atual";
      const planoAtualPrecoBase = planoAtualBackend?.preco || 0;

      // Classificação usa sempre o preço-base do modelo (independente de ciclo),
      // igual ao PlanoService.isDowngrade no backend. A troca só é aplicada
      // imediatamente quando é upgrade e o ciclo escolhido é o mesmo que já está
      // em vigor (mesma regra que o backend usa em agendarTrocaPlano); qualquer
      // outro caso (downgrade ou troca de ciclo) só entra em vigor na renovação.
      const novoPlanoBackend = planosBackend.find((p: any) => p.id === backendPlanoModeloId);
      const novoPlanoPrecoBase = novoPlanoBackend?.preco || 0;

      const isUpgrade = novoPlanoPrecoBase > planoAtualPrecoBase;
      const mesmoCiclo = ciclo === cicloAtual;
      const imediata = isUpgrade && mesmoCiclo;

      setTrocaData({
        planoAtualNome,
        novoPlanoNome: plano.titulo,
        novoPlanoBackendId: backendPlanoModeloId,
        ciclo,
        imediata,
      });
      setTrocaModalOpen(true);
      return;
    }

    // ═══════════════════════════════════════════════════
    // REGRA B: Plano INATIVO ou SEM PLANO → Nova assinatura
    // ═══════════════════════════════════════════════════
    // Envia o id real do PlanoModelo, nao o slug do frontend ("agenda-light").
    // O backend ate resolvia o slug (PlanoService.getByIdOrNome troca "-" por
    // espaco e cai num LIKE), mas so funcionava porque a collation do MySQL
    // ignora acento — "agenda pro" casando com "Agenda Pró". Dependencia
    // fragil demais pra um fluxo que gera cobranca.
    setPlanoSelecionado({
      id: backendPlanoModeloId,
      nome: plano.titulo,
      preco,
      ciclo,
      valorMensal,
    });
    setModalPagamentoOpen(true);
  };

  const handleConfirmTroca = async () => {
    if (!trocaData) return;
    setLoadingTroca(true);
    try {
      const result = await pagamentoService.alterarPlano(trocaData.novoPlanoBackendId, trocaData.ciclo);
      setTrocaModalOpen(false);
      setTrocaData(null);

      if (result && result.status !== "PLANO_ALTERADO") {
        // Backend gerou uma cobrança de ajuste proporcional (proration) — abre o
        // modal de pagamento já com os dados prontos (PIX/Boleto).
        setTrocaPaymentData(result);
        setModalPagamentoOpen(true);
      } else {
        showFeedback("Troca de plano realizada com sucesso!", "success");
        refreshPlano();
      }
    } catch (error: any) {
      showFeedback(error.message || "Erro ao trocar de plano", "error");
    } finally {
      setLoadingTroca(false);
    }
  };

  const handleConfirmPayment = async (
    metodo: MetodoPagamento,
    creditCard?: CreditCardData
  ): Promise<AsaasPaymentResponse | null> => {
    if (!planoSelecionado) return null;

    try {
      const ciclo = planoSelecionado.ciclo;
      const billingType = metodo === "PIX" ? "PIX" : metodo === "CARTAO" ? "CREDIT_CARD" : "BOLETO";

      const result = await pagamentoService.signPlanASAAS(
        planoSelecionado.id,
        ciclo,
        billingType,
        creditCard
      );

      if (result) {
        setPagamentoAtual({
          idAssinatura: result.subscriptionId,
          planoModeloId: planoSelecionado.id,
        });
      }

      return result;
    } catch (error) {
      console.error("Erro ao processar assinatura:", error);
      throw error;
    }
  };

  const handleCloseModal = () => {
    setModalPagamentoOpen(false);
    setPlanoSelecionado(null);
    setTrocaPaymentData(null);
    window.location.reload();
  };

  // Enquanto a empresa ainda está em trial (sem plano ativo), a primeira cobrança
  // gerada (PIX/Boleto) só vence no fim do trial — mesma regra usada pelo backend
  // em AsaasService.criarAssinaturaComPagamento (nextDueDate = dataExpiracao
  // quando ainda está no futuro). Isso só é exibido no modal de pagamento pra
  // deixar claro que o cliente não precisa pagar imediatamente.
  const hojeISO = new Date().toLocaleDateString("en-CA");
  const emTrialComPrazo = !isPlanActive && !!dataExpiracao && dataExpiracao > hojeISO;
  const avisoPagamentoAteData = emTrialComPrazo
    ? new Date(dataExpiracao + "T23:59:59").toLocaleDateString("pt-BR")
    : null;

  return (
    <div className="ds">
      <div className="ds-page-head" style={{ justifyContent: "center", alignItems: "center", textAlign: "center", flexDirection: "column" }}>
        <div>
          <h1>Selecione o plano ideal para sua clínica</h1>
          <p style={{ margin: "6px auto 0" }}>Migrações gratuitas entre planos.</p>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 32, gap: 12 }}>
        <span style={{ fontWeight: isAnnual ? 400 : 700, color: isAnnual ? "var(--ink-faint)" : "var(--ink)", fontSize: 13.6 }}>
          Mensal
        </span>
        <Switch checked={isAnnual} onChange={(_, checked) => setIsAnnual(checked)} />
        <span style={{ position: "relative" }}>
          <span style={{ fontWeight: isAnnual ? 700 : 400, color: isAnnual ? "var(--ink)" : "var(--ink-faint)", fontSize: 13.6 }}>
            Anual
          </span>
          <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", fontSize: 9.5, fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap", marginTop: 2 }}>
            Economize 15%
          </span>
        </span>
      </div>

      {planosBackendErro && !planosBackendLoading && (
        <Alert
          severity="error"
          sx={{ maxWidth: 1000, margin: "0 auto 24px" }}
          action={
            <MuiButton color="inherit" size="small" onClick={fetchPlanos}>
              Tentar novamente
            </MuiButton>
          }
        >
          Não foi possível carregar os valores dos planos. Os preços não são exibidos para evitar
          mostrar um valor diferente do que seria cobrado.
        </Alert>
      )}

      <div className="ds-plan-grid" style={{ maxWidth: 1000, margin: "0 auto" }}>
        {PLANOS_DATA.map((plano) => {
          const planoBackend = getPlanoBackendByNome(plano.titulo);
          const backendId = planoBackend?.id || null;
          const isCurrentPlan = isPlanActive && !cancelado && !!planoModeloId && !!backendId && planoModeloId === backendId;
          const isReativacaoPlanoAtual = cancelado && !!planoModeloId && !!backendId && planoModeloId === backendId;

          return (
            <PlanoCard
              key={plano.id}
              id={plano.id}
              titulo={plano.titulo}
              precoMensal={planoBackend?.preco ?? null}
              precoAnual={planoBackend?.precoAnual ?? null}
              valorMensalEquivalente={planoBackend?.valorMensalEquivalente ?? null}
              funcionalidades={plano.funcionalidades}
              recomendacao={plano.recomendacao}
              isAnnual={isAnnual}
              isAtivo={isCurrentPlan}
              disabled={isCurrentPlan || !planoBackend}
              isPlanActive={isPlanActive}
              isReativacao={isReativacaoPlanoAtual}
              loading={planoContextLoading || planosBackendLoading}
              onAssinar={() => handleAssinar(plano)}
            />
          );
        })}
      </div>

      <PagamentoModal
        isOpen={modalPagamentoOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmPayment}
        planoNome={planoSelecionado?.nome || ""}
        planoPreco={planoSelecionado?.preco || 0}
        planoCiclo={planoSelecionado?.ciclo || "MONTHLY"}
        initialPaymentData={trocaPaymentData}
        avisoPagamentoAteData={avisoPagamentoAteData}
      />

      <Dialog open={trocaModalOpen} onClose={() => setTrocaModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar troca de plano</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            Você está trocando de <strong>{trocaData?.planoAtualNome}</strong> para{" "}
            <strong>{trocaData?.novoPlanoNome}</strong>.
          </p>
          <Alert severity={trocaData?.imediata ? "success" : "info"}>
            {trocaData?.imediata
              ? "Essa troca é aplicada imediatamente — você já passa a ter acesso aos recursos do novo plano agora. Pode ser gerada uma cobrança agora referente apenas à diferença proporcional do período restante (não é uma cobrança cheia nova); sua data de renovação não muda."
              : `Essa troca entra em vigor na próxima renovação${dataExpiracao ? ` (${new Date(dataExpiracao + "T23:59:59").toLocaleDateString("pt-BR")})` : ""} — você continua com os benefícios do plano atual até lá, sem cobrança adicional agora.`}
          </Alert>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setTrocaModalOpen(false)} disabled={loadingTroca}>
            Voltar
          </MuiButton>
          <MuiButton
            onClick={handleConfirmTroca}
            variant="contained"
            disabled={loadingTroca}
          >
            {loadingTroca ? "Processando..." : "Confirmar troca"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PlanosPage;